import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// MUI
import {
  Card, Grid, Modal, Box, TextField, Menu, MenuItem, Tooltip,
  FormControlLabel, Checkbox, CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { getApiErrorMessage } from 'utils/apiErrors';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';

// Material Dashboard components / layout
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';

// App components
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import StatusBadge from 'components/StatusBadge';
import MonoText from 'components/MonoText';
import KanbanToolbar from 'components/KanbanToolbar';
import DealsKanbanBoard from 'components/DealsKanbanBoard';
import ClosedWonConfirmModal from 'components/ClosedWonConfirmModal';
import ClosedLostModal from 'components/CloseLostModal';
import TableActionMenu from 'components/TableActionMenu';

// Hooks, context, utils, theme
import { useAuth } from 'auth/Auth';
import { useTheme } from '@mui/material/styles';
import { Columns3, List } from 'lucide-react';
import PageHeader from 'components/v2/PageHeader';
import SegmentedControl from 'components/v2/SegmentedControl';
import { useActiveApp } from 'shell-v2/apps';
import { usePermission } from 'auth/usePermission';
import useAiRecordScores from 'components/AiAssistant/useAiRecordScores';
import { AiBulkScoreButton, aiListColumns, describeScoringSummary, withAiScores } from 'components/AiAssistant/AiListScoring';
import { useCurrency } from 'context/CurrencyContext';
import useKanbanFilters from 'hooks/useKanbanFilters';
import { formatDate } from 'utils/DateTimeConversion';
import { frontendLogger } from 'utils/logger';
import { validatePersonOrEntityName } from 'utils/crmValidation';
import colors from 'assets/theme/base/colors';


// Add capitalizeFirstLetter function
const capitalizeFirstLetter = (str) => {
  if (str === null || str === undefined) return '';
  str = String(str); // Convert to string
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const ViewToggleIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: active ? 'white' : 'transparent',
  border: active ? 'none' : '1px solid rgba(255, 255, 255, 0.4)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: active ? 'white' : 'rgba(255, 255, 255, 0.15)',
    transform: 'translateY(-1px)',
  },
  '& svg': {
    fontSize: '20px',
    color: active ? theme.palette.info.main : 'white',
    transition: 'color 0.3s ease',
  },
  // v2: the strip is a white card header, so the toggle draws on white. The
  // repeated & outranks theme-v2's strip icon rule.
  ...(theme.pearlUi === 'v2' && {
    backgroundColor: active ? '#E6EFFB' : 'transparent',
    border: `1px solid ${active ? '#B9D0F0' : '#E2E7EF'}`,
    '&:hover': { backgroundColor: active ? '#E6EFFB' : '#F8FAFC', transform: 'none' },
    '&&&& svg': { color: `${active ? '#1453A0' : '#44546F'} !important` },
  }),
}));

const DEAL_SEARCH_FIELDS = ['id', 'name', 'account_name', 'stage', 'currency', 'assigned_to_name'];
const LOCKED_STAGES = ['Closed Won', 'Closed Lost'];

function Deals() {
  const { user, axiosInstance } = useAuth();
  const { canCreate, canDelete, isSupportUser } = usePermission('deals');
  // UI v2 layout (design-reference "Travel · Bookings list"): page header,
  // statistics row, then one card with the view switch, table and board.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { app } = useActiveApp();
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    deal: null,
    deleteRelated: false,
    loading: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deals, setDeals] = useState([]);
  const [filters, setFilters] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accounts, setAccounts] = useState({});
  const { formatCurrency } = useCurrency();
  const [newDeal, setNewDeal] = useState({
    lead_id: '',
    name: '',
    value: '',
    stage: '',
    probability: '',
    closeDate: '',
    account_id: '',
  });
  const navigate = useNavigate();
  const [activities, setActivities] = useState({});
  useEffect(() => {
    fetchDeals();
  }, []);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [dealStats, setDealStats] = useState(null);


  const [viewMode, setViewMode] = useState(() => localStorage.getItem('dealsViewMode') || 'list');
  const [updatingDealId, setUpdatingDealId] = useState(null);

  // Closed Lost flow
  const [closedLostModal, setClosedLostModal] = useState({ open: false, dealId: null });
  const [closedLostComment, setClosedLostComment] = useState('');

  // Closed Won flow
  const [closedWonModal, setClosedWonModal] = useState({ open: false, dealId: null, summary: null });

  // List and Kanban rows carry account_name so search and filters can match on it
  const kanbanRows = useMemo(
      () => deals.map((d) => ({ ...d, account_name: accounts[d.account_id]?.name || d.account_name || '' })),
      [deals, accounts]
  );

  const kanbanFilters = useKanbanFilters({
    objectType: 'deal',            // same value the DataTable already uses
    rows: kanbanRows,
    searchFields: DEAL_SEARCH_FIELDS,
    enabled: viewMode === 'kanban',
  });


  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };


  const fetchDealStats = async () => {
    try {
      const response = await axiosInstance.get('/stats/deals');
      setDealStats(response.data);
    } catch (error) {
      console.error('Error fetching deal stats:', error);
    }
  };

  useEffect(() => {
    fetchDealStats();
  }, []);

  const fetchDeals = async () => {
    setIsLoading(true);
    try {
      const [dealsResponse, accountsResponse] = await Promise.all([
        axiosInstance.get('/deals/?all=true'),
        // Accounts only decorate the list (names); the rows already carry
        // account_name. Without the Accounts module or permission this 403s,
        // which used to throw the deals away too.
        axiosInstance.get('/accounts/').catch(() => ({ data: [] })),
      ]);

      const accountDetails = (accountsResponse.data || []).reduce((acc, account) => {
        acc[account.id] = account;
        return acc;
      }, {});

      // const dealActivities = await Promise.all(
      //   dealsResponse.data.map(async (deal) => {
      //     const activities = await fetchActivities(deal.id);
      //     return { dealId: deal.id, activities };
      //   })
      // );

      // const activitiesMap = dealActivities.reduce((acc, { dealId, activities }) => {
      //   acc[dealId] = activities;
      //   return acc;
      // }, {});

      setAccounts(accountDetails);
      setDeals(dealsResponse.data);
      setFilteredDeals(dealsResponse.data);
      // setActivities(activitiesMap);
    } catch (error) {
      console.error('Error fetching deals:', error);
      setSnackbarMessage(getApiErrorMessage(error, 'Could not load deals. Please try again.'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleInputChange = (e) => {
    setNewDeal({ ...newDeal, [e.target.name]: e.target.value });
  };

  const handleSaveDeal = async (event) => {
    event.preventDefault();
    const nameErr = validatePersonOrEntityName(newDeal.name, 'Deal name');
    if (nameErr) {
      setSnackbarMessage(nameErr);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    try {
      // Add user information to the deal data
      const dealData = {
        ...newDeal,
        created_by_name: deals.name, // Assuming user object has a name property
        updated_by_name: deals.name,
      };

      const response = await axiosInstance.post('/deals/', dealData);
      // Log deal creation (edit for new deal)
      await frontendLogger.logUpdate('Deal', {
        id: response.data.id,
        action: 'create',
        changes: dealData,
        user: deals.name,
        timestamp: new Date().toISOString(),
      });
      handleCloseModal();
      fetchDeals();
      setSnackbarMessage('Deal created successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error creating deal:", error);
      const detail = error.response?.data?.detail;
      setSnackbarMessage(
        typeof detail === 'string' ? detail : 'Error creating deal. Please try again.'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleRowClick = (rowData) => {
    navigate(`/deals/${rowData.id}`);
  };


  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterApply = (column, value) => {
    setFilters({ ...filters, [column]: value });
    handleFilterClose();
  };

  // AI Assistant scores: an optional column, and "Score with AI" on a selection.
  const dealRowIds = useMemo(() => deals.map((d) => d.id), [deals]);
  const aiScores = useAiRecordScores('deals', dealRowIds);
  const [aiSelected, setAiSelected] = useState([]);
  // Memoized: a new rows array on every render would reset the table's paging and sort.
  // kanbanRows carries account_name for search and filters; scores ride on top.
  const dealRows = useMemo(() => withAiScores(kanbanRows, aiScores), [kanbanRows, aiScores.enabled, aiScores.scores]);

  const dealsTableData = {
    columns: [
      ...aiListColumns({ ai: aiScores, selected: aiSelected, setSelected: setAiSelected, rowIds: dealRowIds }),
      {
        Header: "ID",
        accessor: "id",
        width: "5%",
        Cell: ({ row }) => (
            <MDTypography
                component="span"
                onClick={() => handleRowClick(row.original)}
                variant="caption"
                color="info"
                fontWeight="medium"
                sx={{ cursor: 'pointer' }}
            >
              {`#${row.original.id}`}
            </MDTypography>
        ),
      },
      {
        Header: "Deal Name",
        accessor: "name",
        width: "15%",
        Cell: ({ row }) => {
          const dealName = capitalizeFirstLetter(row.original.name);

          return (
            <Box display="flex" alignItems="center">
              <MDTypography
                component="span"
                onClick={() => handleRowClick(row.original)}
                variant="caption"
                color="info"
                fontWeight="medium"
                sx={{
                  cursor: 'pointer',
                  marginRight: 1,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  lineHeight: 1.35,
                  display: 'inline-block',
                  maxWidth: '100%',
                }}
              >
                {dealName}
              </MDTypography>
            </Box>
          );
        }
      },
      { 
        Header: "Est. Deal Value", 
        accessor: "value", 
        width: "15%",
        align: "right",
        Cell: ({ value, row }) => (
          <MonoText align="right">
            {value != null && value !== '' ? formatCurrency(value, row.original.currency) : '-'}
          </MonoText>
        ),
      },
      { 
        Header: "Currency", 
        accessor: "currency", 
        width: "10%",
        Cell: ({ value }) => <MonoText>{value || '-'}</MonoText>,
      },
      {
        Header: "Account",
        accessor: "account_name",
        width: "15%",
        Cell: ({ row }) => {
          const accountId = row.original.account_id;
          const accountName = row.original.account_name;

          return accountName ? (
            <MDTypography
              variant="caption"
              color="info"
              fontWeight="medium"
              sx={{
                cursor: 'pointer',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                lineHeight: 1.35,
                display: 'inline-block',
                maxWidth: '100%',
              }}
              onClick={() => navigate(`/accounts/${accountId}`)} // Navigate to the account details
            >
              {capitalizeFirstLetter(accountName)}
            </MDTypography>
          ) : (
            <MDTypography variant="caption" color="text">
              {/* Loading... */}
            </MDTypography>
          );
        },
      },
      {
        Header: "Est. Close Date", accessor: "estimated_closure_date", width: "10%",
        Cell: ({ value }) => <MonoText>{value ? formatDate(value, 'date') : '-'}</MonoText>,
      },
      {
        Header: "Probability", accessor: "probability", width: "8%", align: "right",
        Cell: ({ value }) => <MonoText align="right">{value != null && value !== '' ? `${value}%` : '-'}</MonoText>,
      },
      {
        Header: "Owner", accessor: "assigned_to_name", width: "10%",
        Cell: ({ value }) => (
          <MDTypography variant="caption" color="text">{value || '-'}</MDTypography>
        ),
      },
      {
        Header: "Type", accessor: "deal_type", width: "8%",
        Cell: ({ value }) => (
          <MDTypography variant="caption" color="text">{value || '-'}</MDTypography>
        ),
      },
      {
        Header: "Created Date", accessor: "created_at", width: "10%",
        Cell: ({ value }) => <MonoText>{value ? formatDate(value) : '-'}</MonoText>,
      },
      {
        Header: "Modified Date", accessor: "updated_at", width: "10%",
        Cell: ({ value }) => <MonoText>{value ? formatDate(value) : '-'}</MonoText>,
      },
      {
        Header: "Stage",
        accessor: "stage",
        width: "10%",
        Cell: ({ value }) => <StatusBadge status={value} size="xs" label={capitalizeFirstLetter(value)} />,
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "5%",
        align: "center",
        disableSortBy: true,
        Cell: ({ row }) =>
            canDelete ? (
                <TableActionMenu
                    row={row.original}
                    onView={handleRowClick}
                    onDelete={() => handleDeleteClick(row.original)}
                />
            ) : (
                <TableActionMenu row={row.original} onView={handleRowClick} />
            ),
      },
    ],
    rows: dealRows,
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('dealsViewMode', mode);
  };

  const showSnack = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleDeleteClick = (deal) => {
    if (isSupportUser) {
      showSnack('Support users are not allowed to delete deals.', 'error');
      return;
    }
    setDeleteDialog({ open: true, deal, deleteRelated: false, loading: false });
  };

  const handleDeleteCancel = () => {
    if (deleteDialog.loading) return; // don't close mid-request
    setDeleteDialog((d) => ({ ...d, open: false }));
  };

  const handleDeleteConfirm = async () => {
    const { deal, deleteRelated } = deleteDialog;
    if (!deal) return;
    if (isSupportUser) {
      showSnack('Support users are not allowed to delete deals.', 'error');
      return;
    }

    setDeleteDialog((d) => ({ ...d, loading: true }));
    try {
      await axiosInstance.delete(`/deals/${deal.id}`, {
        params: { delete_related: deleteRelated },
      });

      // A logging failure must not make a successful delete look like a failure
      try {
        await frontendLogger.logDelete('Deal', {
          id: deal.id,
          name: deal.name,
          user: user?.username,
          deleted_by: user?.id,
        });
      } catch (logError) {
        console.error('Error logging deal deletion:', logError);
      }

      // Remove from the list without refetching
      setDeals((prev) => prev.filter((d) => String(d.id) !== String(deal.id)));
      setFilteredDeals((prev) => prev.filter((d) => String(d.id) !== String(deal.id)));
      fetchDealStats(); // keep the pipeline card in sync

      setDeleteDialog({ open: false, deal: null, deleteRelated: false, loading: false });
      showSnack('Deal deleted successfully');
    } catch (error) {
      console.error('Error deleting deal:', error);
      const detail = error.response?.data?.detail;
      showSnack(typeof detail === 'string' ? detail : 'Error deleting deal. Please try again.', 'error');
      // Keep the dialog open so the user can retry or cancel
      setDeleteDialog((d) => ({ ...d, loading: false }));
    }
  };

  const applyDealPatch = (dealId, patch) => {
    const update = (list) =>
        list.map((d) => (String(d.id) === String(dealId) ? { ...d, ...patch } : d));
    setDeals(update);
    setFilteredDeals(update);
  };

  const updateDealStage = async (dealId, newStage, extra = {}) => {
    const previousStage = deals.find((d) => String(d.id) === String(dealId))?.stage;
    setUpdatingDealId(dealId);
    applyDealPatch(dealId, { stage: newStage }); // optimistic
    try {
      const response = await axiosInstance.put(`/deals/${dealId}`, {
        stage: newStage,
        ...extra,
      });
      applyDealPatch(dealId, response.data);
      // A logging failure must not make a saved update look like a failure
      try {
        await frontendLogger.logUpdate('Deal', {
          id: dealId,
          action: 'kanban_stage_change',
          old_stage: previousStage,
          new_stage: newStage,
          user: user?.username,
          timestamp: new Date().toISOString(),
        });
      } catch (logError) {
        console.error('Error logging deal stage change:', logError);
      }
      showSnack(`Deal stage updated to ${newStage}`);
      fetchDealStats();
      return true;
    } catch (error) {
      console.error('Error updating deal stage:', error);
      applyDealPatch(dealId, { stage: previousStage }); // revert
      const detail = error.response?.data?.detail;
      showSnack(typeof detail === 'string' ? detail : 'Error updating deal stage. Please try again.', 'error');
      return false;
    } finally {
      setUpdatingDealId(null);
    }
  };

  const handleDealDragEnd = async (result) => {
    if (!result.destination) return;
    const dealId = result.draggableId;
    const destination = result.destination.droppableId;
    const deal = deals.find((d) => String(d.id) === String(dealId));
    if (!deal) return;

    if (LOCKED_STAGES.includes(deal.stage)) {
      showSnack(`Cannot move deal from ${deal.stage} stage as it is a final stage.`, 'warning');
      return;
    }

    if (destination === 'Closed Lost') {
      setClosedLostModal({ open: true, dealId });
      return;
    }

    if (destination === 'Closed Won') {
      const numericValue = Number(deal.value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        showSnack('Closed Won deals require a Value greater than 0. Update Value before closing.', 'error');
        return;
      }
      try {
        const response = await axiosInstance.get(`/deals/${dealId}/quotations-summary`);
        if (response.data?.has_quotations) {
          setClosedWonModal({ open: true, dealId, summary: response.data });
          return;
        }
        await updateDealStage(dealId, 'Closed Won');
      } catch (error) {
        console.error('Error fetching quotations summary:', error);
        showSnack('Error updating deal stage', 'error');
      }
      return;
    }

    await updateDealStage(dealId, destination);
  };

  const handleClosedLostCancel = () => {
    setClosedLostModal({ open: false, dealId: null });
    setClosedLostComment('');
  };

  const handleClosedLostSubmit = async () => {
    if (!closedLostComment.trim()) {
      showSnack('Please provide a comment for closing the deal as Lost.', 'warning');
      return;
    }
    const { dealId } = closedLostModal;
    const ok = await updateDealStage(dealId, 'Closed Lost', { closed_lost_comment: closedLostComment });
    if (ok) handleClosedLostCancel();
  };

  const closeClosedWonModal = () =>
      setClosedWonModal({ open: false, dealId: null, summary: null });

  const handleClosedWonCancel = closeClosedWonModal;

  const handleClosedWonConfirm = async () => {
    const { dealId } = closedWonModal;
    const ok = await updateDealStage(dealId, 'Closed Won');
    if (ok) closeClosedWonModal();
  };

  // Shared by the classic and v2 layouts below, so both render the same
  // statistics, actions, table and board.
  const kpiCards = [
    <ComplexStatisticsCard
      key="kpi-0"
      color="dark"
      icon="attach_money"
      title="Deal Pipeline Value"
      count={dealStats ? formatCurrency(dealStats.total_pipeline_value, dealStats.total_pipeline_value_currency) : formatCurrency(0)}
      percentage={{
        color: dealStats?.percentage_color || "success",
        amount: dealStats ? `${dealStats.percentage_change > 0 ? '+' : ''}${dealStats.percentage_change}%` : '0%',
        label: "than last month",
      }}
    />,
    <ComplexStatisticsCard
      key="kpi-1"
      icon="leaderboard"
      title="Win Rate"
      count={`${Math.round(deals.filter(deal => deal.stage === "Closed Won").length / deals.length * 100 || 0)}%`}
      percentage={{
        color: (() => {
          const today = new Date();
          const currentQuarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
          const lastQuarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 - 3, 1);
          const lastQuarterEnd = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 0);

          const thisQuarterDeals = deals.filter(deal => deal.created_at && new Date(deal.created_at) >= currentQuarterStart);
          const lastQuarterDeals = deals.filter(deal => deal.created_at && new Date(deal.created_at) >= lastQuarterStart && new Date(deal.created_at) <= lastQuarterEnd);

          const thisQuarterWinRate = thisQuarterDeals.length > 0
            ? thisQuarterDeals.filter(deal => deal.stage === "Closed Won").length / thisQuarterDeals.length * 100
            : 0;
          const lastQuarterWinRate = lastQuarterDeals.length > 0
            ? lastQuarterDeals.filter(deal => deal.stage === "Closed Won").length / lastQuarterDeals.length * 100
            : 0;

          const percentage = (thisQuarterWinRate - lastQuarterWinRate).toFixed(1);
          return percentage >= 0 ? "success" : "error";
        })(),
        amount: (() => {
          const today = new Date();
          const currentQuarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
          const lastQuarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 - 3, 1);
          const lastQuarterEnd = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 0);

          const thisQuarterDeals = deals.filter(deal => deal.created_at && new Date(deal.created_at) >= currentQuarterStart);
          const lastQuarterDeals = deals.filter(deal => deal.created_at && new Date(deal.created_at) >= lastQuarterStart && new Date(deal.created_at) <= lastQuarterEnd);

          const thisQuarterWinRate = thisQuarterDeals.length > 0
            ? thisQuarterDeals.filter(deal => deal.stage === "Closed Won").length / thisQuarterDeals.length * 100
            : 0;
          const lastQuarterWinRate = lastQuarterDeals.length > 0
            ? lastQuarterDeals.filter(deal => deal.stage === "Closed Won").length / lastQuarterDeals.length * 100
            : 0;

          const percentage = (thisQuarterWinRate - lastQuarterWinRate).toFixed(1);
          return percentage == 0 ? `0.0%` : `${percentage > 0 ? '+' : ''}${percentage}%`;
        })(),
        label: "than last quarter",
      }}
    />,
    <ComplexStatisticsCard
      key="kpi-2"
      color="success"
      icon="store"
      title="Deals Won"
      count={deals.filter(deal => deal.stage === "Closed Won").length}
      percentage={{
        color: (() => {
          const today = new Date();
          const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const startOfYesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          const endOfYesterday = new Date(startOfYesterday);
          endOfYesterday.setHours(23, 59, 59, 999);

          // Changed from created_at to updated_at
          const todayWon = deals.filter(deal =>
            deal.updated_at &&
            new Date(deal.updated_at) >= startOfToday &&
            deal.stage === "Closed Won"
          ).length;

          // Changed from created_at to updated_at
          const yesterdayWon = deals.filter(deal =>
            deal.updated_at &&
            new Date(deal.updated_at) >= startOfYesterday &&
            new Date(deal.updated_at) <= endOfYesterday &&
            deal.stage === "Closed Won"
          ).length;

          const percentage = yesterdayWon === 0 ? 0 : ((todayWon - yesterdayWon) / yesterdayWon * 100).toFixed(1);
          return percentage >= 0 ? "success" : "error";
        })(),
        amount: (() => {
          const today = new Date();
          const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const startOfYesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          const endOfYesterday = new Date(startOfYesterday);
          endOfYesterday.setHours(23, 59, 59, 999);

          // Changed from created_at to updated_at
          const todayWon = deals.filter(deal =>
            deal.updated_at &&
            new Date(deal.updated_at) >= startOfToday &&
            deal.stage === "Closed Won"
          ).length;

          // Changed from created_at to updated_at
          const yesterdayWon = deals.filter(deal =>
            deal.updated_at &&
            new Date(deal.updated_at) >= startOfYesterday &&
            new Date(deal.updated_at) <= endOfYesterday &&
            deal.stage === "Closed Won"
          ).length;

          const percentage = yesterdayWon === 0 ? 0 : ((todayWon - yesterdayWon) / yesterdayWon * 100).toFixed(1);
          return percentage == 0 ? `0.0%` : `${percentage > 0 ? '+' : ''}${percentage}%`;
        })(),
        label: "than yesterday",
      }}
    />,
    <ComplexStatisticsCard
      key="kpi-3"
      color="primary"
      icon="person_add"
      title="New Deals This Week"
      count={
        deals.filter(deal => {
          if (!deal.created_at) return false;
          const createdDate = new Date(deal.created_at);
          const today = new Date();
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          return !isNaN(createdDate) && createdDate >= startOfWeek && createdDate <= endOfWeek;
        }).length
      }
      percentage={{
        color: (() => {
          const today = new Date();
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          const startOfLastWeek = new Date(startOfWeek);
          startOfLastWeek.setDate(startOfWeek.getDate() - 7);
          const endOfLastWeek = new Date(endOfWeek);
          endOfLastWeek.setDate(endOfWeek.getDate() - 7);

          const thisWeekNew = deals.filter(deal => deal.created_at && new Date(deal.created_at) >= startOfWeek && new Date(deal.created_at) <= endOfWeek).length;
          const lastWeekNew = deals.filter(deal => deal.created_at && new Date(deal.created_at) >= startOfLastWeek && new Date(deal.created_at) <= endOfLastWeek).length;

          const percentage = lastWeekNew === 0 ? 0 : ((thisWeekNew - lastWeekNew) / lastWeekNew * 100).toFixed(1);
          return percentage >= 0 ? "success" : "error";
        })(),
        amount: (() => {
          const today = new Date();
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          const startOfLastWeek = new Date(startOfWeek);
          startOfLastWeek.setDate(startOfWeek.getDate() - 7);
          const endOfLastWeek = new Date(endOfWeek);
          endOfLastWeek.setDate(endOfWeek.getDate() - 7);

          const thisWeekNew = deals.filter(deal => deal.created_at && new Date(deal.created_at) >= startOfWeek && new Date(deal.created_at) <= endOfWeek).length;
          const lastWeekNew = deals.filter(deal => deal.created_at && new Date(deal.created_at) >= startOfLastWeek && new Date(deal.created_at) <= endOfLastWeek).length;

          const percentage = lastWeekNew === 0 ? 0 : ((thisWeekNew - lastWeekNew) / lastWeekNew * 100).toFixed(1);
          return percentage == 0 ? `0.0%` : `${percentage > 0 ? '+' : ''}${percentage}%`;
        })(),
        label: "than last week",
      }}
    />,
  ];
  const dealActions = (
    <>
      {viewMode === 'list' && (
        <AiBulkScoreButton
          ai={aiScores}
          selected={aiSelected}
          onDone={(summary) => {
            setAiSelected([]);
            showSnack(describeScoringSummary(summary), summary.message ? 'warning' : 'success');
          }}
        />
      )}
    </>
  );
  const dealsBody = (
    <>
      {isLoading ? (
          <TableSkeleton />
      ) : filteredDeals.length === 0 ? (
          <EmptyState moduleName="Deal" canCreate={canCreate} />
      ) : viewMode === 'list' ? (
          <DataTable
              table={dealsTableData}
              isSorted={true}
              noEndBorder
              canSearch
              exportCSV={true}
              entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
              showTotalEntries
              pagination={{ variant: "gradient", color: "info" }}
              objectType="deal"
              onFilterClear={() => fetchDeals()}
          />
      ) : (
          <>
            <KanbanToolbar
                filters={kanbanFilters}
                searchPlaceholder="Search Deal"
                itemLabel="deal"
                objectLabel="Deals"
            />
            {kanbanFilters.filtersLoading ? (
                <TableSkeleton />
            ) : kanbanFilters.visibleRows.length > 0 ? (
                <DealsKanbanBoard
                    deals={kanbanFilters.visibleRows}
                    accounts={accounts}
                    formatCurrency={formatCurrency}
                    onDragEnd={handleDealDragEnd}
                    onDealClick={(deal) => handleRowClick(deal)}
                    updatingDealId={updatingDealId}
                />
            ) : (
                <MDBox py={8} textAlign="center">
                  <MDTypography variant="h6">No deals match your filters</MDTypography>
                  <MDButton variant="outlined" color="info" size="small" sx={{ mt: 2 }} onClick={kanbanFilters.clearAll}>
                    Clear filters
                  </MDButton>
                </MDBox>
            )}
          </>
      )}
    </>
  );

  const dealsLabel = app?.labels?.deals || 'Deals';
  const v2Main = (
    <MDBox>
      <PageHeader
        title={dealsLabel}
        summary={isLoading ? null : `${filteredDeals.length} ${dealsLabel.toLowerCase()}`}
        actions={dealActions}
      />
      <Grid container spacing={2} mb={2.5}>
        {kpiCards.map((cardEl) => (
          <Grid item xs={12} sm={6} lg={3} key={cardEl.key}>
            {cardEl}
          </Grid>
        ))}
      </Grid>
      <Card>
        <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1.5} px={2.5} pt={2.5}>
          <SegmentedControl
            label="View"
            value={viewMode}
            onChange={handleViewModeChange}
            options={[
              { value: 'list', label: 'List', icon: List },
              { value: 'kanban', label: 'Board', icon: Columns3 },
            ]}
          />
        </MDBox>
        <MDBox pt={1}>{dealsBody}</MDBox>
      </Card>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={3} pb={3}>
        {/* Statistics Cards */}
        <Grid container spacing={6}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[0]}
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[1]}
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[2]}
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[3]}
            </MDBox>
          </Grid>
        </Grid>
        <br />
        <br />
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox display="flex" alignItems="center" gap={2}>
                  <MDTypography variant="h6" color="white">
                    Deals
                  </MDTypography>
                  <MDBox display="flex" gap={1}>
                    <Tooltip title="List View" placement="bottom">
                      <ViewToggleIcon active={viewMode === 'list'} onClick={() => handleViewModeChange('list')}>
                        <ViewListIcon />
                      </ViewToggleIcon>
                    </Tooltip>
                    <Tooltip title="Kanban View" placement="bottom">
                      <ViewToggleIcon active={viewMode === 'kanban'} onClick={() => handleViewModeChange('kanban')}>
                        <ViewKanbanIcon />
                      </ViewToggleIcon>
                    </Tooltip>
                  </MDBox>
                </MDBox>
                {dealActions}
              </MDBox>
              <MDBox pt={3}>
                {dealsBody}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      )}

      {/* Modal for adding new deal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <MDTypography id="modal-modal-title" variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
              Add New Deal
            </MDTypography>
            <IconButton
              aria-label="Close"
              onClick={handleCloseModal}
              sx={{ color: '#FFFFFF' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 4 }}>
          <form onSubmit={handleSaveDeal}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="Lead ID" name="lead_id" value={newDeal.lead_id} onChange={handleInputChange} variant="outlined" margin="normal" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Name" name="name" value={newDeal.name} onChange={handleInputChange} variant="outlined" margin="normal" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Value" name="value" value={newDeal.value} onChange={handleInputChange} variant="outlined" margin="normal" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Stage" name="stage" value={newDeal.stage} onChange={handleInputChange} variant="outlined" margin="normal" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Accounts" name="accounts" value={newDeal.accounts} onChange={handleInputChange} variant="outlined" margin="normal" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Close Date" name="closeDate" type="date" value={newDeal.closeDate} onChange={handleInputChange} variant="outlined" margin="normal" InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
            <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end">
              <MDButton onClick={handleCloseModal} color="secondary" sx={{ marginRight: 2 }}>
                Close
              </MDButton>
              <MDButton type="submit" variant="gradient" color="info">
                Save
              </MDButton>
            </MDBox>
          </form>
          </Box>
        </Box>
      </Modal>

      {/* Filter Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleFilterClose}
      >
        <MenuItem onClick={() => handleFilterApply("stage", "")}>Clear All Filters</MenuItem>
        <MenuItem onClick={() => handleFilterApply("stage", "Proposal")}>Stage: Proposal</MenuItem>
        <MenuItem onClick={() => handleFilterApply("stage", "Negotiation")}>Stage: Negotiation</MenuItem>
        <MenuItem onClick={() => handleFilterApply("stage", "Closed Won")}>Stage: Closed Won</MenuItem>
      </Menu>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage || 'Deal name copied to clipboard!'}
        </Alert>
      </Snackbar>

      <ClosedLostModal
          isOpen={closedLostModal.open}
          loading={closedLostModal.open && String(updatingDealId) === String(closedLostModal.dealId)}
          onClose={handleClosedLostCancel}
          comment={closedLostComment}
          onCommentChange={setClosedLostComment}
          onSubmit={handleClosedLostSubmit}
      />

      <ClosedWonConfirmModal
          isOpen={closedWonModal.open}
          summary={closedWonModal.summary}
          loading={closedWonModal.open && String(updatingDealId) === String(closedWonModal.dealId)}
          onClose={handleClosedWonCancel}
          onConfirm={handleClosedWonConfirm}
      />

      <Modal
          open={deleteDialog.open}
          onClose={deleteDialog.loading ? undefined : handleDeleteCancel}
          aria-labelledby="delete-deal-title"
      >
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400, bgcolor: 'background.paper', boxShadow: 24,
          borderRadius: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <Box className="pearl-popup-header" sx={{ backgroundColor: colors.popupHeader.background, px: 4, py: 3 }}>
            <MDTypography id="delete-deal-title" variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
              Delete Deal
            </MDTypography>
          </Box>

          <Box sx={{ p: 4 }}>
            <MDTypography variant="body1" mb={3} sx={{ color: '#34495e' }}>
              Are you sure you want to delete{' '}
              <strong>{deleteDialog.deal?.name || `Deal ${deleteDialog.deal?.id ?? ''}`}</strong>?
              This action cannot be undone.
            </MDTypography>

            <FormControlLabel
                control={
                  <Checkbox
                      checked={deleteDialog.deleteRelated}
                      onChange={(e) => setDeleteDialog((d) => ({ ...d, deleteRelated: e.target.checked }))}
                      color="primary"
                      disabled={deleteDialog.loading}
                  />
                }
                label="Delete related records"
            />

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                  onClick={handleDeleteCancel}
                  variant="outlined"
                  color="secondary"
                  disabled={deleteDialog.loading}
                  sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                  onClick={handleDeleteConfirm}
                  variant="gradient"
                  color="error"
                  disabled={deleteDialog.loading}
              >
                {deleteDialog.loading ? <CircularProgress size={22} color="inherit" /> : 'Delete'}
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

    </DashboardLayout>
  );
}

export default Deals;